import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, MapPin, CreditCard, Banknote, Coffee, UtensilsCrossed, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import Layout from "@/components/layout/Layout";

// Session storage keys (must match useSessionMode.ts)
const SESSION_KEY = "session_mode_data";
const SESSION_EXPIRY_KEY = "session_mode_expiry";

// Helper to get current session data
const getSessionData = () => {
  try {
    const expiry = sessionStorage.getItem(SESSION_EXPIRY_KEY);
    if (!expiry || Date.now() >= parseInt(expiry, 10)) {
      return null;
    }
    const savedSession = sessionStorage.getItem(SESSION_KEY);
    if (!savedSession) return null;
    return JSON.parse(savedSession);
  } catch {
    return null;
  }
};

// Helper to build return URL with session parameters
const buildSessionUrlWithFallback = (basePath: string): string => {
  try {
    const expiry = sessionStorage.getItem(SESSION_EXPIRY_KEY);
    if (expiry && Date.now() < parseInt(expiry, 10)) {
      const savedSession = sessionStorage.getItem(SESSION_KEY);
      if (savedSession) {
        const session = JSON.parse(savedSession);
        const params = new URLSearchParams();
        
        if (session.mode === "mesa" && session.tableNumber) {
          params.set("mesa", session.tableNumber.toString());
          if (session.locationCode) {
            params.set("store", session.locationCode);
          }
        } else if (session.mode === "pickup") {
          params.set("type", "pickup");
          if (session.locationCode) {
            params.set("store", session.locationCode);
          }
        }
        
        const queryString = params.toString();
        if (queryString) return `${basePath}?${queryString}`;
      }
    }
    
    return basePath;
  } catch {
    return basePath;
  }
};

const PedidoConfirmado = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [orderNumber, setOrderNumber] = useState<number | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<string>("local");
  const [sessionData, setSessionData] = useState<any>(null);

  // Scroll to top and load session data on mount
  useEffect(() => {
    window.scrollTo(0, 0);
    setSessionData(getSessionData());
  }, []);

  const handleNavigateToHome = useCallback(() => {
    navigate(buildSessionUrlWithFallback("/"));
  }, [navigate]);

  useEffect(() => {
    // Check if coming from Stripe
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      // Verify Stripe session and update order
      verifyStripeSession(sessionId);
    } else if (location.state?.orderNumber) {
      setOrderNumber(location.state.orderNumber);
      setPaymentMethod(location.state.paymentMethod || "local");
    }
  }, [searchParams, location.state]);

  const verifyStripeSession = async (sessionId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke("verify-payment", {
        body: { sessionId },
      });

      if (error) throw error;

      if (data?.orderNumber) {
        setOrderNumber(data.orderNumber);
        setPaymentMethod("online");
      }
    } catch (error) {
      console.error("Error verifying payment:", error);
    }
  };

  return (
    <Layout>
      <section className="py-12 md:py-20 bg-background min-h-[70vh] flex items-center">
        <div className="container mx-auto px-4 max-w-lg text-center">
          <div className="mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 mb-6">
              <CheckCircle2 className="h-10 w-10 text-green-600" />
            </div>
            <h1 className="font-serif text-3xl md:text-4xl font-bold text-primary mb-3">
              ¡Pedido Confirmado!
            </h1>
            {orderNumber && (
              <p className="text-muted-foreground">
                Número de pedido: <span className="font-bold text-secondary text-2xl">#{orderNumber}</span>
              </p>
            )}
            
            {/* Indicador visual de mesa/modo */}
            {sessionData?.mode === "mesa" && sessionData?.tableNumber && (
              <div className="mt-4 inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full">
                <UtensilsCrossed className="h-4 w-4" />
                <span className="font-semibold">Mesa {sessionData.tableNumber}</span>
              </div>
            )}
            {sessionData?.mode === "pickup" && (
              <div className="mt-4 inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full">
                <Coffee className="h-4 w-4" />
                <span className="font-semibold">Para Llevar</span>
              </div>
            )}
          </div>

          <Card className="border-border mb-8">
            <CardContent className="p-6 space-y-4">
              {paymentMethod === "online" ? (
                <div className="flex items-center gap-3 text-left p-4 bg-green-50 rounded-xl">
                  <CreditCard className="h-6 w-6 text-green-600" />
                  <div>
                    <p className="font-medium text-green-700">Pago completado</p>
                    <p className="text-sm text-green-600">Tu pedido está pagado y confirmado</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3 text-left p-4 bg-amber-50 rounded-xl">
                  <Banknote className="h-6 w-6 text-amber-600" />
                  <div>
                    <p className="font-medium text-amber-700">Pago en local</p>
                    <p className="text-sm text-amber-600">Paga en efectivo o tarjeta al recoger</p>
                  </div>
                </div>
              )}

              <div className="text-left space-y-3 pt-4">
                <div className="flex items-start gap-3">
                  <Clock className="h-5 w-5 text-secondary mt-0.5" />
                  <div>
                    <p className="font-medium text-primary">Tiempo estimado</p>
                    <p className="text-sm text-muted-foreground">10-15 minutos aprox.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-secondary mt-0.5" />
                  <div>
                    <p className="font-medium text-primary">
                      {sessionData?.mode === "mesa" ? "Servicio a mesa" : "Recogida en barra"}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {sessionData?.mode === "mesa" 
                        ? "Te lo llevaremos a tu mesa"
                        : "Te avisaremos cuando esté listo"}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-3">
            <Button
              variant="outline"
              size="lg"
              className="w-full"
              onClick={handleNavigateToHome}
            >
              <Home className="h-5 w-5 mr-2" />
              Volver al inicio
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default PedidoConfirmado;
